import { useEffect, useMemo } from 'react';
import { type QueryClient, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { uniq } from 'lodash';
import { type DAMNode, toDAMNodes, useTreeStore } from '@/hooks/data/tree/useTreeStore';
import { QueryKey, buildTreePathQueryKey, buildTreeQueryKey } from '@/lib/queryKeys';
import { getIdFromPath, getNode, getRootNodes } from '@/services/tree.service';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { type TreeRecursiveChildrenNode } from '@/types/tree';
import { useCapabilities } from '@/hooks/useCapabilities';
import { useTreeContext } from '@/context/TreeContext';

// Without this a primed entry is refetched the moment its key is observed, since staleTime is 0.
const TREE_STALE_TIME = 30_000;

/** Seeds the path cache from the tree store so it resolves synchronously instead of over the network. */
export const primeTreePathCache = (
  queryClient: QueryClient,
  nodePath: Array<DAMNode> | null,
  pathArray: Array<string> | null | undefined,
) => {
  if (!nodePath || !pathArray || pathArray.length === 0) {
    return;
  }
  queryClient.setQueryData(
    buildTreePathQueryKey(pathArray),
    nodePath.map(({ id, slug, type }) => ({ id, slug, type })),
  );
};

/**
 * Combine rootNodes with the separately-fetched childNodes into a recursive
 * tree by resolving each node's children from a lookup map, starting from
 * rootNodes.
 *
 * This is order-independent by design. The previous implementation reduced
 * childNodes into an accumulator and spliced each fetched node in by array
 * order, which depended on `sidebarTreeOpenIds` insertion order. Closing then
 * reopening a parent re-appends its key after its still-open children, so the
 * children were processed before their parent existed in the accumulator and
 * their data was silently dropped — the chevron showed "open" but nothing
 * rendered (DM-1411). Keying off rootNodes removes that dependency entirely.
 */
export const combineTreeNodes = (
  rootNodes: Array<TreeRecursiveChildrenNode>,
  childNodes: Array<TreeRecursiveChildrenNode | undefined>,
): Array<TreeRecursiveChildrenNode> => {
  const nodeMap = new Map(
    childNodes.filter((node): node is TreeRecursiveChildrenNode => node !== undefined).map((node) => [node.id, node]),
  );

  const build = (nodes: Array<TreeRecursiveChildrenNode>): Array<TreeRecursiveChildrenNode> =>
    nodes.map((node) => {
      const fetched = nodeMap.get(node.id);
      return {
        ...node,
        children: fetched ? build(fetched.children ?? []) : (node.children ?? []),
      };
    });

  return build(rootNodes);
};

export const usePathData = (pathArray: Array<string>, folderOpened: boolean) => {
  const getIdFromPathFn = useAuthenticatedQueryFn(getIdFromPath);
  // Check if pathArray is valid
  const {
    data: routeIds,
    isLoading: isPathLoading,
    isFetching: isPathFetching,
  } = useQuery({
    queryKey: buildTreePathQueryKey(pathArray),
    queryFn: () => {
      return getIdFromPathFn({
        path: pathArray, // Ensure the path is an empty array if pathArray is undefined or null
      });
    },
    enabled: folderOpened && pathArray?.length > 0,
    staleTime: TREE_STALE_TIME,
  });

  const folderIds = routeIds?.slice(0, -1).map((item: { id: any }) => item.id) ?? [];

  const { albumId, folderId } =
    routeIds?.length > 0
      ? routeIds[routeIds.length - 1].type === 'album'
        ? { albumId: routeIds[routeIds.length - 1].id, folderId: undefined }
        : routeIds[routeIds.length - 1].type === 'folder'
          ? { albumId: undefined, folderId: routeIds[routeIds.length - 1].id }
          : { albumId: undefined, folderId: undefined }
      : { albumId: undefined, folderId: undefined };

  // Once path resolution has settled (fetch failed, or succeeded without matching a
  // folder/album) without producing an id, the requested path doesn't exist. Without this,
  // folderId/albumId are indistinguishable from "still resolving" and the gallery is stuck
  // showing loading skeletons forever (DM-2115).
  const isPathNotFound =
    folderOpened && pathArray.length > 0 && !isPathLoading && !isPathFetching && !folderId && !albumId;

  return {
    folderIds,
    albumId,
    folderId,
    isPathNotFound,
  };
};

export const useTree = () => {
  const queryClient = useQueryClient();
  const context = useTreeContext();
  const getRootNodesFn = useAuthenticatedQueryFn(getRootNodes);
  const getNodeFn = useAuthenticatedQueryFn(getNode);
  const setRootFolders = useTreeStore((state) => state.setRootFolders);
  const { canViewSidePanels } = useCapabilities();
  const { sidebarTreeOpenIds, reusableTreeOpenIds, updatedIds } = context;
  const { isAuthenticated } = useAuth0();

  const { data: rootNodes, isLoading: isRootNodesLoading } = useQuery({
    queryKey: [QueryKey.Tree],
    queryFn: () => getRootNodesFn(null),
    // Prevent loading tree as a guest until we have a dedicated guest home page
    enabled: isAuthenticated && canViewSidePanels,
  });

  const { data: childNodes } = useQueries({
    queries: uniq([...Object.keys(reusableTreeOpenIds), ...Object.keys(sidebarTreeOpenIds), ...updatedIds]).map(
      (nId) => ({
        queryKey: buildTreeQueryKey(nId),
        queryFn: () => getNodeFn({ nodeId: nId }),
        staleTime: Infinity,
        refetchOnMount: true,
      }),
    ),
    combine: (results) => ({
      data: results.map((result) => result.data),
      isLoading: results.some((result) => result.isLoading),
    }),
  });

  const getNodeWithChildren = (id: string) => {
    return queryClient.fetchQuery({
      queryKey: [QueryKey.Tree, id, 'with-children'],
      queryFn: () => getNodeFn({ nodeId: id, includeChildren: true }),
      staleTime: 0,
    });
  };

  const tree = useMemo(() => combineTreeNodes(rootNodes ?? [], childNodes), [childNodes, rootNodes]);

  // The store's path lookups answer for whatever the sidebar is showing, so both read the same tree.
  useEffect(() => {
    setRootFolders(toDAMNodes(tree));
  }, [tree, setRootFolders]);

  return {
    ...context,
    tree,
    rootNodes,
    isRootNodesLoading,
    getNodeWithChildren,
  };
};
